import { useState } from "react";
import type { MeResponse } from "@edu/shared";
import { guessTimeZone, zoneLabel } from "../../lib/time-zone.ts";
import { TimeZoneDialog } from "./TimeZoneDialog.tsx";

/**
 * Требование подтвердить часовой пояс, пока он не подтверждён.
 *
 * ⚠️ **Переписано 20.08.2026 из `TimeZoneBar` по отзыву преподавателя.** Что
 * изменилось и почему:
 *
 * 1. **Первое подтверждение стало окном, а полоса осталась под ним запасным
 *    слоем.** Отзыв дословный: «высветилось как шторка сверху, и я прям на
 *    автомате щёлкнул ок», просьба дословная: «наоборот надо навязчиво».
 *    Лестница здесь намеренная: окно поперёк экрана при каждой загрузке
 *    кабинета, а закрывшему его остаётся полоса, то есть тихо, но не молча
 * 2. **Вопроса про расхождение больше нет вовсе.** Раньше человеку с
 *    подтверждённой зоной, чьи часы показывают другую, полоса ЗАДАВАЛА вопрос
 *    «обновить?», и под этот вопрос была написана память об отказе в
 *    `localStorage` с окном в сутки: без неё человек под VPN отбивался бы от
 *    него каждый день. Владелец 20.08.2026 усомнился ровно в этом месте:
 *    «удобно, если приложение подсказывает, что ты приехал в другой город, но
 *    как быть с впн?»
 *
 *    Ответ: **никак, потому что вопрос не задаётся.** Отличить переезд от VPN
 *    браузер не умеет и не научится, значит любой ответ на такой вопрос это
 *    догадка, которую мы просим подтвердить человека. Расхождение теперь просто
 *    НАЗВАНО фактом в строке «время в зоне …» на расписании, рядом с временами,
 *    которые от него зависят, и там же стоит «Изменить». У факта нет неверного
 *    ответа, отбиваться от него не нужно, и вместе с вопросом уехала вся память
 *    об отказах: восемьдесят строк, существовавших только затем, чтобы вопрос
 *    был терпимым
 *
 * ⚠️ Спрашивать в КОМНАТЕ УРОКА запрещено, и держится это тем, что компонент
 * стоит в каркасе кабинета, а комната лежит вне него (`Shell`). Человек,
 * спешащий на занятие, которого уже ждут, нажмёт что угодно, лишь бы убрать
 * окно, и мы получим подтверждённой зону аэропортового вайфая.
 *
 * ⚠️ Принятое ограничение: ученик может не заходить в кабинет вовсе, он живёт
 * по ссылке в урок. Тогда вопрос не задастся ему никогда, и это нормально: без
 * подтверждённой зоны он видит время в зоне браузера, то есть обычно верной.
 */
export function TimeZoneGate(props: { me: MeResponse; onDone: (me: MeResponse) => void }) {
  /**
   * Окно открыто. Начальное значение `true`, то есть окно встаёт САМО при
   * каждой загрузке кабинета, пока пояс не подтверждён. Это и есть «навязчиво».
   */
  const [asking, setAsking] = useState(true);

  // Пояс подтверждён: ни окна, ни полосы. Дверь к смене живёт на расписании
  if ((props.me.timeZone ?? null) !== null) return null;
  /**
   * ⚠️ Неполная сессия (9.6 `docs/account-adopt-design.md`): этот браузер вошёл
   * ссылками не всех преподавателей ученика, и пояс отсюда не меняется, сервер
   * ответит `session_partial`. Пояс сдвинул бы время занятий и у тех
   * преподавателей, чьих ссылок здесь не открывали. Ни окна, ни полосы:
   * просьба, которую нельзя выполнить, хуже отсутствия просьбы. Время при этом
   * показано в зоне браузера, как у всех без подтверждённого пояса.
   */
  if (props.me.sessionPartial === true) return null;

  if (asking) {
    return <TimeZoneDialog me={props.me} onDone={props.onDone} onClose={() => setAsking(false)} />;
  }

  return (
    <div className="tzbar" role="status">
      <span className="tzbar__text">
        Часовой пояс не указан, время занятий показано в зоне{" "}
        <b title={guessTimeZone()}>{zoneLabel(guessTimeZone())}</b>. От пояса зависит время каждого
        занятия.
      </span>
      {/*
        Обычный вес, не акцентный. Полоса стоит НАД рабочей областью и висит над
        любым разделом кабинета: акцент в ней конкурировал бы с главной кнопкой
        того экрана, который под ней открыт, а правило «одна акцентная на экран»
        считает экран целиком, вместе с хромом. Акцент у этой просьбы есть, но
        живёт он в окне, у которого свой экран.
      */}
      <button type="button" className="btn" onClick={() => setAsking(true)}>
        Указать
      </button>
    </div>
  );
}
